import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface FormContextValue {
  values: Record<string, unknown>;
  setValue: (slug: string, value: unknown) => void;
}

const FormContext = createContext<FormContextValue | null>(null);

export interface FormProviderProps {
  children: React.ReactNode;
  /** Optional initial values (e.g. from layout component_default_values) */
  initialValues?: Record<string, unknown>;
}

export function FormProvider({ children, initialValues = {} }: FormProviderProps): React.ReactElement {
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);

  const setValue = useCallback((slug: string, value: unknown) => {
    setValues((prev) =>
      prev[slug] === value ? prev : { ...prev, [slug]: value }
    );
  }, []);

  const value = useMemo<FormContextValue>(() => ({ values, setValue }), [values, setValue]);

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export function useFormContext(): FormContextValue {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}

/**
 * Optional: use FormContext if it exists, but don't throw when outside.
 * Useful for components that can work both with and without form context.
 */
export function useFormContextOptional(): FormContextValue | null {
  return useContext(FormContext);
}
